import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "components/ui/AppLayout";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import { integrationsApi } from "api/integrations";
import { orgApi } from "api/org";

function CopyField({ label, value, mono = true }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="mb-3">
      <label className="font-caption text-xs block mb-1" style={{ color: "var(--color-text-secondary)" }}>
        {label}
      </label>
      <div className="flex items-center gap-2 rounded-md border px-3 py-2" style={{ borderColor: "var(--color-border)", background: "var(--color-muted)" }}>
        <code className={`font-caption text-xs flex-1 break-all ${mono ? "" : ""}`} style={{ color: "var(--color-foreground)" }}>
          {value || "—"}
        </code>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 font-caption text-xs hover:underline"
          style={{ color: "var(--color-primary)", cursor: "pointer", flexShrink: 0 }}
        >
          <Icon name={copied ? "Check" : "Copy"} size={13} color="currentColor" />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

function cardTitle(icon, title, sub) {
  return (
    <div className="flex items-center gap-2 mb-1">
      <Icon name={icon} size={16} color="var(--color-primary)" />
      <h2 className="font-heading font-semibold text-base" style={{ color: "var(--color-foreground)" }}>
        {title}
      </h2>
    </div>
  );
}

/**
 * Integrations center (Sprint 15 QA) — every lead source in one place:
 * website lead form (token + snippet), IndiaMART webhook, WhatsApp status,
 * JustDial via the phone channel, Meta Pages guidance, CSV import.
 */
export default function Integrations() {
  const [integrations, setIntegrations] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [error, setError] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [snippetCopied, setSnippetCopied] = useState(false);

  const load = useCallback(async () => {
    const [data, wa] = await Promise.all([integrationsApi.getIntegrations(), orgApi.getWhatsappStatus()]);
    setIntegrations(data);
    setWhatsappStatus(wa);
  }, []);

  useEffect(() => {
    load()
      .catch((e) => setError(e?.message || "Unable to load integrations."))
      .finally(() => setIsLoading(false));
  }, [load]);

  const regenerate = async () => {
    setRegenerating(true);
    setError("");
    try {
      await integrationsApi.regenerateLeadForm();
      await load();
    } catch (e) {
      setError(e?.message || "Could not regenerate the token.");
    } finally {
      setRegenerating(false);
    }
  };

  const testLead = async () => {
    setTesting(true);
    setTestResult(null);
    setError("");
    try {
      const result = await integrationsApi.testWebsiteLead();
      setTestResult(result);
    } catch (e) {
      setTestResult({ error: e?.message || "Test failed." });
    } finally {
      setTesting(false);
    }
  };

  const pushMock = async () => {
    setPushing(true);
    setPushResult(null);
    setError("");
    try {
      const result = await integrationsApi.mockIndiaMart();
      setPushResult(result);
    } catch (e) {
      setPushResult({ error: e?.message || "Mock push failed." });
    } finally {
      setPushing(false);
    }
  };

  const webhookUrl = integrations?.website?.webhookUrl || "";
  const token = integrations?.website?.token || "";
  const snippet = `<!-- CRM lead form — paste anywhere on your website -->
<form id="crm-lead-form">
  <input name="fullName" placeholder="Full name" required />
  <input name="phone" placeholder="Phone" required />
  <input name="email" placeholder="Email" />
  <input name="company" placeholder="Company" />
  <textarea name="message" placeholder="Message"></textarea>
  <button type="submit">Send enquiry</button>
</form>
<script>
  (function () {
    var form = document.getElementById('crm-lead-form');
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var data = new FormData(form), body = {};
      data.forEach(function (v, k) { body[k] = v; });
      fetch('${webhookUrl}', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-lead-form-token': '${token}'
        },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (r.ok) { form.reset(); alert('Thanks! We will get back to you.'); }
        else { alert('Sorry, something went wrong.'); }
      }).catch(function () { alert('Sorry, something went wrong.'); });
    });
  })();
</script>`;

  const waLive = whatsappStatus?.provider === "meta";
  const waPhone = whatsappStatus?.phone;

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
            Integrations
          </h1>
          <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
            Connect every place your customers come from — every source lands as a lead in the same pipeline.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border px-4 py-3 flex items-center justify-between gap-4" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{error}</p>
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              iconSize={14}
              onClick={() => {
                setError("");
                setIsLoading(true);
                load()
                  .catch((e) => setError(e?.message || "Unable to load integrations."))
                  .finally(() => setIsLoading(false));
              }}
            >
              Retry
            </Button>
          </div>
        )}

        {isLoading && (
          <div className="rounded-xl border px-4 py-10 text-center mb-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading integrations...</p>
          </div>
        )}

        {/* WhatsApp */}
        <section className="rounded-xl border mb-4 p-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          {cardTitle("MessageCircle", "WhatsApp", "")}
          <p className="font-caption text-xs mb-4" style={{ color: "var(--color-text-secondary)" }}>
            Customer conversations land in the Inbox — consent-gated, official Meta Business API.
          </p>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-caption text-xs font-medium" style={{ background: waPhone ? "#DCFCE7" : "#FEF3C7", color: waPhone ? "#15803D" : "#B45309" }}>
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: waPhone ? "#15803D" : "#F59E0B" }} />
              {waPhone || "No number yet"}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-caption text-xs font-medium" style={{ background: waLive ? "#DCFCE7" : "#FEF3C7", color: waLive ? "#15803D" : "#B45309" }}>
              <Icon name={waLive ? "CheckCircle2" : "FlaskConical"} size={12} color="currentColor" />
              {waLive ? "Live — Business API" : "Dev mode"}
            </span>
            <Link to="/settings" className="font-caption text-sm hover:underline" style={{ color: "var(--color-primary)" }}>
              Configure in Settings →
            </Link>
          </div>
        </section>

        {/* Website lead form */}
        <section className="rounded-xl border mb-4 p-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          {cardTitle("Globe", "Website lead form", "")}
          <p className="font-caption text-xs mb-4" style={{ color: "var(--color-text-secondary)" }}>
            Copy this form + snippet into any page of your website — enquiries appear in Leads instantly, each one also
            creates a call-back task. Duplicate numbers are skipped automatically.
          </p>
          <CopyField label="Webhook URL" value={webhookUrl} />
          <CopyField label="Form token (keep private — regenerating it disables the old one)" value={token} />
          <div className="mb-4">
            <label className="font-caption text-xs block mb-1" style={{ color: "var(--color-text-secondary)" }}>
              Embeddable code — paste before &lt;/body&gt;
            </label>
            <div className="rounded-md border" style={{ borderColor: "var(--color-border)", background: "var(--color-muted)" }}>
              <pre className="p-3 overflow-auto text-xs leading-relaxed" style={{ color: "var(--color-foreground)", maxHeight: 260 }}>
                {snippet}
              </pre>
            </div>
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(snippet);
                  setSnippetCopied(true);
                  setTimeout(() => setSnippetCopied(false), 2000);
                } catch {
                  /* noop */
                }
              }}
              className="mt-2 inline-flex items-center gap-1 font-caption text-xs hover:underline"
              style={{ color: "var(--color-primary)", cursor: "pointer" }}
            >
              <Icon name="Copy" size={13} color="currentColor" /> {snippetCopied ? "Copied!" : "Copy full snippet"}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" iconName="Send" onClick={testLead} disabled={testing}>
              {testing ? "Sending..." : "Send test lead"}
            </Button>
            <Button variant="ghost" size="sm" onClick={regenerate} disabled={regenerating}>
              {regenerating ? "Regenerating..." : "Regenerate token"}
            </Button>
          </div>
          {testResult && (
            <p className="font-caption text-xs mt-3" style={{ color: testResult.error ? "var(--color-error)" : "var(--color-success)" }}>
              {testResult.error
                ? `Test failed: ${testResult.error}`
                : testResult.duplicates > 0
                  ? "Test lead already exists (duplicate skipped) — dedupe works."
                  : `Test lead created — check the Leads page (${testResult.leads?.[0]?.fullName}).`}
            </p>
          )}
        </section>

        {/* IndiaMART */}
        <section className="rounded-xl border mb-4 p-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          {cardTitle("ShoppingCart", "IndiaMART", "")}
          <p className="font-caption text-xs mb-4" style={{ color: "var(--color-text-secondary)" }}>
            Buyer enquiries pushed from IndiaMART via webhook. {integrations?.indiamart?.tokenConfigured ? "Live partner token is configured." : "No partner token configured yet — webhook is open for demo mode only."}
          </p>
          <CopyField label="Webhook URL (give this to your IndiaMART partner)" value={integrations?.indiamart?.webhookUrl || ""} />
          <Button variant="outline" size="sm" iconName="Zap" onClick={pushMock} disabled={pushing}>
            {pushing ? "Pushing..." : "Push demo leads"}
          </Button>
          {pushResult && (
            <p className="font-caption text-xs mt-3" style={{ color: pushResult.error ? "var(--color-error)" : "var(--color-success)" }}>
              {pushResult.error ? `Push failed: ${pushResult.error}` : `Demo leads pushed — ${pushResult.created} created, ${pushResult.duplicates} duplicates.`}
            </p>
          )}
        </section>

        {/* JustDial */}
        <section className="rounded-xl border mb-4 p-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          {cardTitle("PhoneCall", "JustDial", "")}
          <p className="font-caption text-xs mb-4" style={{ color: "var(--color-text-secondary)" }}>
            JustDial doesn't offer a public lead API — its enquiries arrive as calls, SMS or WhatsApp on your business
            number, and our phone + WhatsApp channels pick them up automatically as contacts and threads. For bulk
            history, export from the JustDial dashboard and use CSV import.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/leads" className="font-caption text-sm hover:underline" style={{ color: "var(--color-primary)" }}>
              CSV import on Leads →
            </Link>
          </div>
        </section>

        {/* Meta Pages */}
        <section className="rounded-xl border mb-4 p-5" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          {cardTitle("Instagram", "Instagram & Facebook DMs", "")}
          <p className="font-caption text-xs mb-4" style={{ color: "var(--color-text-secondary)" }}>
            Page DMs from the same official Meta Business App as WhatsApp will merge into the Inbox. Coming soon —
            contact your account manager to enable the connector for your workspace.
          </p>
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-caption text-xs font-medium" style={{ background: "#FEF3C7", color: "#B45309" }}>
            <Icon name="Clock" size={12} color="currentColor" /> Coming soon
          </span>
        </section>
      </div>
    </AppLayout>
  );
}